/**
 * Cognito 認証サービス
 * 
 * amazon-cognito-identity-js を使用した認証管理
 */

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
} from 'amazon-cognito-identity-js';

const userPool = new CognitoUserPool({
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
  ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
});

/**
 * ログイン
 */
export function signIn(email, password) {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    const authDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });

    user.authenticateUser(authDetails, {
      onSuccess: (result) => {
        resolve({
          idToken: result.getIdToken().getJwtToken(),
          accessToken: result.getAccessToken().getJwtToken(),
          refreshToken: result.getRefreshToken().getToken(),
        });
      },
      onFailure: (err) => {
        reject(err);
      },
      newPasswordRequired: (userAttributes) => {
        // 初回ログイン時のパスワード変更が必要
        resolve({
          challengeName: 'NEW_PASSWORD_REQUIRED',
          user,
          userAttributes,
        });
      },
    });
  });
}

/**
 * 新しいパスワードの設定 (初回ログイン時)
 */
export function completeNewPassword(user, newPassword) {
  return new Promise((resolve, reject) => {
    user.completeNewPasswordChallenge(newPassword, {}, {
      onSuccess: (result) => {
        resolve({
          idToken: result.getIdToken().getJwtToken(),
          accessToken: result.getAccessToken().getJwtToken(),
          refreshToken: result.getRefreshToken().getToken(),
        });
      },
      onFailure: (err) => {
        reject(err);
      },
    });
  });
}

/**
 * ログアウト
 */
export function signOut() {
  const user = userPool.getCurrentUser();
  if (user) {
    user.signOut();
  }
}

/**
 * 現在のセッション (ID Token) を取得
 */
export function getSession() {
  return new Promise((resolve, reject) => {
    const user = userPool.getCurrentUser();
    if (!user) {
      reject(new Error('No current user'));
      return;
    }

    user.getSession((err, session) => {
      if (err) {
        reject(err);
        return;
      }

      if (!session.isValid()) {
        reject(new Error('Session is not valid'));
        return;
      }

      resolve({
        idToken: session.getIdToken().getJwtToken(),
        accessToken: session.getAccessToken().getJwtToken(),
        email: session.getIdToken().payload.email,
      });
    });
  });
}

/**
 * 認証済みかチェック
 */
export function isAuthenticated() {
  return getSession()
    .then(() => true)
    .catch(() => false);
}

/**
 * 現在のユーザー情報を取得
 */
export function getCurrentUser() {
  return userPool.getCurrentUser();
}

export function signUp({ email, password, name, orgType }) {
  return new Promise((resolve, reject) => {
    const attributeList = [
      new CognitoUserAttribute({ Name: 'email', Value: email }),
      new CognitoUserAttribute({ Name: 'name', Value: name }),
      new CognitoUserAttribute({ Name: 'custom:orgType', Value: orgType }),
    ];
    userPool.signUp(email, password, attributeList, null, (err, result) => {
      if (err) { reject(err); return; }
      resolve(result);
    });
  });
}

export function confirmSignUp(email, code) {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: userPool });
    user.confirmRegistration(code, true, (err, result) => {
      if (err) { reject(err); return; }
      resolve(result);
    });
  });
}

export function resendConfirmationCode(email) {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: userPool });
    user.resendConfirmationCode((err, result) => {
      if (err) { reject(err); return; }
      resolve(result);
    });
  });
}
